import { ComparisonTable } from "./ComparisonTable";
import { SectionHeader } from "./SectionHeader";
import type { Theme } from "./theme";

const IN_PRACTICE = [
  "speed and capacity",
  "external perspective",
  "confidence in progressing candidates",
];

const WITH_CLARITY = [
  "confidence increases",
  "decisions improve",
  "reliance on external input reduces",
];

const RARION_ENABLES = [
  "handle more hiring internally",
  "apply consistent evaluation across all candidates",
  "make decisions with greater confidence",
  "use agencies selectively, where they add real value",
];

const COMPARISON_ROWS: [string, string][] = [
  ["external dependency", "internal capability"],
  ["inconsistent assessment", "structured, consistent evaluation"],
  ["speed-driven shortlists", "evidence-based decisions"],
  ["no retained learning", "reusable hiring knowledge"],
];

export function AgencyFit({ theme }: { theme: Theme }) {
  return (
    <section
      style={{
        padding: "clamp(72px, 12vw, 120px) 0",
        borderTop: `1px solid ${theme.border}`,
        background: theme.surface,
      }}
    >
      <SectionHeader
        theme={theme}
        kicker="Where agencies fit"
        title="Where agencies fit — and where they don't."
      >
        Most organisations rely on agencies when they lack time, confidence, or clarity in the
        hiring process. They are often used as the fastest way to move forward.
      </SectionHeader>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 5vw, 40px)" }}>
        <Callout theme={theme}>Companies believe they are buying access to talent.</Callout>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 64,
            marginTop: 56,
            alignItems: "stretch",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: theme.fgMute,
                marginBottom: 16,
                fontFamily: theme.mono,
              }}
            >
              In practice, agencies are also used for
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {IN_PRACTICE.map((p, i) => (
                <li
                  key={p}
                  style={{
                    padding: "16px 0",
                    borderTop: i === 0 ? `1px solid ${theme.border}` : "none",
                    borderBottom: `1px solid ${theme.border}`,
                    fontSize: 17,
                    color: theme.fg,
                    letterSpacing: -0.2,
                  }}
                >
                  {p}
                </li>
              ))}
            </ul>
            <p
              style={{
                fontSize: 16,
                color: theme.fgMute,
                lineHeight: 1.6,
                marginTop: 24,
                fontStyle: "italic",
              }}
            >
              The hiring decision, however, still sits with your team.
            </p>
          </div>

          <StatBlock theme={theme} />
        </div>

        <div
          style={{
            marginTop: 80,
            paddingTop: 56,
            borderTop: `1px solid ${theme.border}`,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 64,
            alignItems: "start",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: theme.fgMute,
                marginBottom: 16,
                fontFamily: theme.mono,
              }}
            >
              When there is clear role definition and consistent assessment
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {WITH_CLARITY.map((w, i) => (
                <li
                  key={w}
                  style={{
                    padding: "16px 0",
                    borderTop: i === 0 ? `1px solid ${theme.border}` : "none",
                    borderBottom: `1px solid ${theme.border}`,
                    fontSize: 17,
                    color: theme.fg,
                    letterSpacing: -0.2,
                  }}
                >
                  {w}
                </li>
              ))}
            </ul>
            <p
              style={{
                fontSize: 22,
                fontWeight: 500,
                color: theme.fg,
                letterSpacing: -0.3,
                lineHeight: 1.4,
                marginTop: 24,
              }}
            >
              The need for agencies <span style={{ color: theme.accentFg }}>changes</span>.
            </p>
          </div>

          <div>
            <div
              style={{
                fontSize: 12,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: theme.accentFg,
                marginBottom: 16,
                fontFamily: theme.mono,
                fontWeight: 500,
              }}
            >
              Rarion enables organisations to
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {RARION_ENABLES.map((r, i) => (
                <li
                  key={r}
                  style={{
                    padding: "16px 0",
                    borderTop: i === 0 ? `1px solid ${theme.accentBorder}` : "none",
                    borderBottom: `1px solid ${theme.accentBorder}`,
                    fontSize: 17,
                    color: theme.fg,
                    letterSpacing: -0.2,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      background: theme.accent,
                      flexShrink: 0,
                    }}
                  />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{ marginTop: 80 }}>
          <ComparisonTable theme={theme} rows={COMPARISON_ROWS} />
          <p
            style={{
              fontSize: 22,
              fontWeight: 500,
              color: theme.fg,
              letterSpacing: -0.3,
              lineHeight: 1.4,
              marginTop: 40,
              textAlign: "center",
            }}
          >
            Reduce dependency without sacrificing{" "}
            <span style={{ color: theme.accentFg }}>quality</span> or{" "}
            <span style={{ color: theme.accentFg }}>speed</span>.
          </p>
        </div>
      </div>
    </section>
  );
}

function Callout({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 40,
        padding: "28px 32px",
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderLeft: `3px solid ${theme.accent}`,
        borderRadius: theme.radius,
        fontSize: 22,
        fontWeight: 500,
        color: theme.fg,
        letterSpacing: -0.3,
        lineHeight: 1.4,
        maxWidth: 760,
      }}
    >
      {children}
    </div>
  );
}

function StatBlock({ theme }: { theme: Theme }) {
  return (
    <div
      style={{
        padding: "40px 32px",
        background: theme.bg,
        border: `1px solid ${theme.accentBorder}`,
        borderRadius: theme.radius * 1.5,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 80% 20%, ${theme.accentSoft}, transparent 60%)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative" }}>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: theme.fgMute,
            marginBottom: 12,
            fontFamily: theme.mono,
          }}
        >
          Where agency candidates come from
        </div>
        <div
          style={{
            fontSize: "clamp(56px, 12vw, 88px)",
            fontWeight: 700,
            color: theme.accentFg,
            letterSpacing: -3,
            lineHeight: 1,
            marginBottom: 16,
          }}
        >
          90%+
        </div>
        <div
          style={{
            fontSize: 18,
            color: theme.fg,
            letterSpacing: -0.2,
            lineHeight: 1.5,
            maxWidth: 360,
          }}
        >
          of agency candidates are sourced from <span style={{ fontWeight: 600 }}>LinkedIn</span>{" "}
          and <span style={{ fontWeight: 600 }}>job postings</span>.
        </div>
      </div>
    </div>
  );
}
